import React, { useState, useMemo } from "react";
import QRCode from "react-qr-code";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import getDelphiNumber from "../../../../utils/getDelphiNumber";
import "./style.scss";
import { AiOutlineSafety } from "react-icons/ai";
import format from "date-fns/format";
import CardDescription from "./CardDescription";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";
import ModalComponent from "../../../../features/ModalComponent";
import useFacility from "../../../../utils/hooks/useFacility";

import { ECARD_RECHARGE } from "../../../routes";
import LoadingSpinner from "../../../../features/Loading/LoadingSpinner";
import { useTranslation } from "react-i18next";
import EAButton from "../../shared/Buttons/EAButton";
import BalanceCard, { BalanceCardItem } from "./BalanceCard";

const AccountCardPanel: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: State) => state.auth.userData);
  const loading = useSelector((state: State) => state.auth.loading);
  const isDesktop = useDesktopSize();
  const facilityId = useFacility();
  const [open, setOpen] = useState(false);
  const ecardQr = useSelector((state: State) => state.config.configuration.ecard_showBarCode);
  const ecardRegisterIncome = useSelector((state: State) => state.config.configuration.ecard_RegisterIncome);

  // Sprawdzenie czy data minęła
  const isCardExpired = userData.closeDate && new Date(userData.closeDate).getTime() < new Date().getTime();
  const isFacility273 = facilityId === "273";
  const isFacility1209 = facilityId === "1209";
  const showCashierOnlyMessage = isCardExpired && (isFacility273 || isFacility1209);

  // Gromadzenie danych balansów do wyświetlenia
  const balanceItems: BalanceCardItem[] = useMemo(() => {
    const items: BalanceCardItem[] = [];

    if (userData.captionDepositBalance && userData.captionDepositBalance !== "0.00") {
      items.push({
        caption: userData.descriptionDepositBalance || t("depozyt"),
        balance: userData.captionDepositBalance,
        description: userData.descriptionDepositBalance || t("depozyt"),
      });
    }

    if (userData.captionBalanceI && userData.captionBalanceI !== "0.00") {
      items.push({
        caption: userData.descriptionBalanceI || "VAT I",
        balance: userData.captionBalanceI,
        description: userData.descriptionBalanceI || "VAT I",
      });
    }

    if (userData.captionBalanceII && userData.captionBalanceII !== "0.00") {
      items.push({
        caption: userData.descriptionBalanceII || "VAT II",
        balance: userData.captionBalanceII,
        description: userData.descriptionBalanceII || "VAT II",
      });
    }

    if (userData.captionBalanceIII && userData.captionBalanceIII !== "0.00") {
      items.push({
        caption: userData.descriptionBalanceIII || "VAT III",
        balance: userData.captionBalanceIII,
        description: userData.descriptionBalanceIII || "VAT III",
      });
    }

    return items;
  }, [userData, t]);

  return (
    <>
      {loading ? (
        <div className="flex-between-center">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {isDesktop ? (
            <div className="account-card">
              <div className=" account-card-title flex-between-center">
                <h4>{t("moja_karta")}</h4>
                {/* <EAButton title={t("dowiedz_sie_wiecej")} startIcon={<MdOutlineInfo />} variant="text"/> */}
              </div>

              <div className="account-card-container">
                {ecardQr && (
                  <div className="account-card-qr-section">
                    <div className="qr flex-center-center flex-column">
                      <QRCode value={`EA_${getDelphiNumber(userData.account)}`} size={100} />
                      <b>{`EA_${getDelphiNumber(userData.account)}`}</b>
                    </div>
                  </div>
                )}

                <div className="account-card-info">
                  <div className="account-card-info-item">
                    <span className="label">{t("upust")}:</span>
                    <span className="value">
                      <CardDescription />
                    </span>
                  </div>
                  <div className="account-card-info-item">
                    <span className="label">{t("waznosc")}:</span>
                    <span className="value">
                      {userData.closeDate ? (
                        <span>{format(userData.closeDate, "dd.MM.yyyy")}r.</span>
                      ) : (
                        <span>{t("brak")}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="account-card-balances">
                  {balanceItems.length > 0 ? (
                    balanceItems.map((item, index) => <BalanceCard key={index} items={[item]} />)
                  ) : (
                    <div className="account-card-no-balance">
                      <span>{t("brak_srodkow")}</span>
                    </div>
                  )}

                  {showCashierOnlyMessage && (
                    <div className="account-card-cashier-message">
                      <span className="caption text-color-gray">Doładowanie możliwe tylko w kasie.</span>
                    </div>
                  )}
                </div>

                {!showCashierOnlyMessage && ecardRegisterIncome && balanceItems.length >= 0 && (
                  <EAButton
                    title={t("doladuj_karte")}
                    onClick={() => (window.location.href = ECARD_RECHARGE)}
                    startIcon={<AiOutlineSafety />}
                    color="secondary"
                    fullWidth
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="account-card-mobile account-card-title flex-between-center">
                <h4>{t("moja_karta")}</h4>
                <EAButton title={t("zobacz")} onClick={() => setOpen(!open)} color="secondary" />
              </div>

              {open && (
                <ModalComponent
                  footer={true}
                  open={open}
                  closeModal={() => setOpen(false)}
                  title={t("moja_ekarta")}
                  onAccept={() => setOpen(false)}
                >
                  <div className="account-card-mobile-content">
                    {ecardQr && (
                      <div className="account-card-qr-section">
                        <div className="qr flex-center-center flex-column">
                          <QRCode value={getDelphiNumber(userData.account)} size={100} />
                          <b>{getDelphiNumber(userData.account)}</b>
                        </div>
                      </div>
                    )}

                    <div className="account-card-info">
                      <div className="account-card-info-item">
                        <span className="label">{t("upust")}:</span>
                        <span className="value">
                          <CardDescription />
                        </span>
                      </div>
                      <div className="account-card-info-item">
                        <span className="label">{t("waznosc")}:</span>
                        <span className="value">
                          {userData.closeDate ? (
                            <span>{format(userData.closeDate, "dd.MM.yyyy")}r.</span>
                          ) : (
                            <span>{t("brak")}</span>
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="account-card-balances">
                      {balanceItems.length > 0 ? (
                        balanceItems.map((item, index) => <BalanceCard key={index} items={[item]} />)
                      ) : (
                        <div className="account-card-no-balance">
                          <span>{t("brak_srodkow")}</span>
                        </div>
                      )}

                      {showCashierOnlyMessage && (
                        <div className="account-card-cashier-message">
                          <span className="caption text-color-gray">Doładowanie możliwe tylko w kasie.</span>
                        </div>
                      )}
                    </div>

                    {!showCashierOnlyMessage && ecardRegisterIncome && balanceItems.length > 0 && (
                      <EAButton
                        title={t("doladuj_karte")}
                        onClick={() => {
                          setOpen(false);
                          window.location.href = ECARD_RECHARGE;
                        }}
                        startIcon={<AiOutlineSafety />}
                        color="secondary"
                        fullWidth
                      />
                    )}
                  </div>
                </ModalComponent>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default AccountCardPanel;
